/* @flow */

import {useMutation} from '@apollo/react-hooks';

import type {
    McfxCompaniesUnlock as McfxCompaniesUnlockMutation,
    McfxCompaniesUnlockVariables as McfxCompaniesUnlockMutationVariables,
    McfxCompaniesUnlockInput,
    GetUnlockInfo as GetUnlockInfoQuery,
} from 'nutshell-graphql-types';

import MCFX_COMPANIES_UNLOCK from '../../graphql/mutations/unlock-companies.graphql';
import MCFX_COMPANY_FRAGMENT from '../../graphql/fragments/mcfx-company-fragment.graphql';
import GET_UNLOCK_INFO from '../../graphql/queries/get-unlock-info.graphql';

export function useUnlockCompanies(): (input: McfxCompaniesUnlockInput) => Promise<*> {
    const [unlockCompanies] = useMutation<
        McfxCompaniesUnlockMutation,
        McfxCompaniesUnlockMutationVariables
    >(MCFX_COMPANIES_UNLOCK);

    return (input) => {
        return unlockCompanies({
            variables: {input},
            update: (proxy, mutationResults) => {
                // Keep the unlock count data up-to-date
                const cacheResult: ?GetUnlockInfoQuery = proxy.readQuery({
                    query: GET_UNLOCK_INFO,
                });

                if (!cacheResult || !cacheResult.unlockInfo) {
                    return;
                }

                proxy.writeQuery({
                    query: GET_UNLOCK_INFO,
                    data: {
                        unlockInfo: {
                            ...cacheResult.unlockInfo,
                            unlockedThisMonth:
                                cacheResult.unlockInfo.unlockedThisMonth +
                                input.mcfxCompanyIds.length,
                        },
                    },
                });

                // If unlocking one company from the sidebar, update the cache so the display is up-to-date
                if (input.mcfxCompanyIds.length === 1) {
                    const companyId = input.mcfxCompanyIds[0];

                    // Read the data from our cache for this fragment.
                    const companyFragment = proxy.readFragment({
                        id: companyId,
                        fragmentName: 'McfxCompanyFragment',
                        fragment: MCFX_COMPANY_FRAGMENT,
                    });

                    if (companyFragment) {
                        const updatedCompany =
                            mutationResults &&
                            mutationResults.data &&
                            mutationResults.data.mcfxCompaniesUnlock &&
                            mutationResults.data.mcfxCompaniesUnlock.mcfxCompanies[0];

                        if (!updatedCompany) return;

                        proxy.writeFragment({
                            id: companyId,
                            fragmentName: 'McfxCompanyFragment',
                            fragment: MCFX_COMPANY_FRAGMENT,
                            data: updatedCompany,
                        });
                    }
                }
            },
        });
    };
}
